import { create } from 'zustand';

/**
 * Supported surfaces for Bizy contextual `/` invocation. V1 scope is the
 * recognition give box and comment reply composer. New surfaces should add
 * their own enum value here and own their context shape.
 */
export type BizyContextualSurface = 'recognition' | 'comment';

/**
 * Loose, surface-specific metadata that gets forwarded to Bizy so the
 * assistant knows what the user is working on (recipients, post author,
 * etc.). Kept as a generic record because the backend owns interpretation.
 */
export type BizyContextualSurfaceData = Record<string, string | number | boolean | string[] | null | undefined>;

export interface BizyContextualInsertRequest {
	surface: BizyContextualSurface;
	surfaceData: BizyContextualSurfaceData;
	/** Text label describing the recipient / addressee (e.g. "Alex", "Alex and Jordan") */
	recipientLabel?: string;
	/**
	 * User-perspective starter message auto-sent to Bizy on open so Bizy
	 * actively starts the conversation (asking the right follow-up question
	 * based on surface context) instead of sitting silent.
	 */
	starterMessage: string;
	/**
	 * Called when Bizy is dismissed (panel close), so the surface can return
	 * focus to the original input if it's still mounted. Insert / Send-now
	 * actions on draft cards do NOT route through here — they go through
	 * the typed UI block dispatcher to either the active comment composer
	 * (`useBizyActiveComposersStore`) or the give-box (`GiveBoxEvent.Open`).
	 */
	onDismiss?: () => void;
	/**
	 * True if the side panel was already open when `/` invoked Bizy. Used by
	 * the UI block action dispatcher to decide whether to close the panel
	 * after a completion action: when `/` opened the panel we close on
	 * completion (the panel's job is done); when the user already had Bizy
	 * open, we leave it open so they can keep chatting.
	 */
	panelWasAlreadyOpen: boolean;
}

interface BizyContextualInsertStore {
	pendingContext: BizyContextualInsertRequest | null;
	setPendingContext: (context: BizyContextualInsertRequest) => void;
	clearPendingContext: () => void;
}

export const useBizyContextualInsertStore = create<BizyContextualInsertStore>((set, get) => ({
	pendingContext: null,
	setPendingContext: (context) => set({ pendingContext: context }),
	clearPendingContext: () => {
		const current = get().pendingContext;
		set({ pendingContext: null });
		current?.onDismiss?.();
	},
}));
